// AnalyticsView.tsx - Workflow Analytics with auto-detect unified search
import React, { useState, useCallback, useRef, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';
const FONT = "'DM Sans', 'Segoe UI', -apple-system, sans-serif";

// ─── Types ───────────────────────────────────────────────────────────────────

interface AnalyticsViewProps {
    getAccessToken: () => Promise<string>;
    onStatusUpdate: (status: { type: 'success' | 'error' | null; message: string }) => void;
}

type NodeStatus = 'completed' | 'active' | 'pending' | 'rejected' | 'returned' | 'skipped';

interface TaskSummary {
    taskId: number; assignedUserName: string; assignedUserEmail: string;
    assignedCompanyName?: string; assignedCompanyId?: number; taskStatus: string;
    assignedAt: string; completedAt: string | null; completedBy?: string;
    comments?: string | null;
}
interface CompanyCycle {
    cycleNumber: number; companyName: string; companyId: number;
    approvers: TaskSummary[]; reviewers: TaskSummary[];
    reviewerCompanyName?: string | null; reviewerCompanyId?: number | null;
}
interface GateBranch {
    gateId: number; gateNumber: string; locationName?: string; isExcluded: boolean;
    exclusionReason?: string | null;
    reviewerTasks: TaskSummary[]; approverTasks: TaskSummary[];
}
interface AuditEntry {
    action: string;
    performedBy: string;
    performedByEmail: string;
    companyName?: string;
    performedAt: string;
    assignedTo?: string;
    comments?: string;
    taskType?: string;
}

interface WorkflowGraphData {
    currentState: string;
    submitter: { name: string; email: string; companyName: string; submittedAt: string };
    companyCycles: CompanyCycle[]; gateBranches: GateBranch[];
    securityStage: { tasks: TaskSummary[] } | null; totalTasks: number;
    auditTrail?: AuditEntry[];
    workflowPasses?: WorkflowPass[];
}
interface WorkflowPass {
    passNumber: number;
    companyApprovers: TaskSummary[];
    companyReviewers: TaskSummary[];
    gateBranches: GateBranch[];
    securityTasks: TaskSummary[];
    companyName: string;
    reviewerCompanyName?: string | null;
    resubmitEvent?: { performer: string; timestamp: string } | null;
    returnEvent?: { performer: string; timestamp: string; stage: string; comments?: string } | null;
}
interface ReassignContext { taskType: string; stageLabel: string; gateId?: number; gateNumber?: string; }
interface EmailSearchResult {
    id: number; requesterName: string; companyName: string;
    consignmentDetails: string; passIdentificationName: string;
    submittedAt: string; currentState: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function resolveTaskGroupStatus(tasks: TaskSummary[], currentState: string, activeStates: string[]): NodeStatus {
    if (tasks.length === 0) return 'pending';
    const hasCompleted = tasks.some(t => t.taskStatus === 'COMPLETED');
    const hasRejected = tasks.some(t => t.taskStatus === 'REJECTED');
    const hasPending = tasks.some(t => t.taskStatus === 'PENDING');
    const allCancelledByUser = tasks.every(t => t.taskStatus === 'CANCELLED_BY_USER');
    if (hasRejected) return 'rejected';
    if (hasCompleted && !hasPending) return 'completed';
    if (hasPending && activeStates.includes(currentState)) return 'active';
    if (hasPending) return 'pending';
    if (allCancelledByUser) return 'skipped';
    if (tasks.every(t => t.taskStatus === 'CANCELLED')) return 'completed';
    return 'completed';
}
function formatTime(dateStr: string | null | undefined): string | undefined {
    if (!dateStr) return undefined;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function getPerformerName(tasks: TaskSummary[]): string | undefined {
    if (!tasks || tasks.length === 0) return undefined;
    const names = [...new Set(
        tasks.map(t => t.assignedUserName || t.assignedUserEmail?.split('@')[0] || '').filter(Boolean)
    )];
    if (names.length === 0) return undefined;
    if (names.length === 1) return names[0];
    return names.join(', ');
}
function getCompletedTime(tasks: TaskSummary[]): string | undefined {
    const completed = tasks.find(t => t.taskStatus === 'COMPLETED');
    return formatTime(completed?.completedAt || completed?.assignedAt);
}
function friendlyState(state: string): string {
    const map: Record<string, string> = {
        SUBMITTED: 'Submitted', COMPANY_APPROVER_PENDING: 'Company Approver',
        COMPANY_REVIEWER_PENDING: 'Company Reviewer', GATE_REVIEWER_PENDING: 'Gate Reviewer',
        GATE_APPROVER_PENDING: 'Gate Approver', SECURITY_APPROVER_PENDING: 'Security Approver',
        PARTIALLY_APPROVED: 'Partially Approved', RETURNED: 'Returned',
    };
    return map[state] || state.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Split a single pass's CA/CR tasks into sequential stages ────────────
// When the same company handles multiple review rounds (external1 & external2
// are the same company), the backend may deliver all tasks in one pass.
// This helper splits them into ordered stages by taskId so the graph shows
// each round as a distinct forward step instead of lumping them together.
interface CompanyStage {
    approvers: TaskSummary[];
    reviewers: TaskSummary[];
}
function splitPassIntoStages(approvers: TaskSummary[], reviewers: TaskSummary[]): CompanyStage[] {
    if (approvers.length === 0 && reviewers.length === 0) return [];

    // Merge and sort all tasks by taskId to get chronological order
    const allTasks = [
        ...approvers.map(t => ({ task: t, kind: 'CA' as const })),
        ...reviewers.map(t => ({ task: t, kind: 'CR' as const })),
    ].sort((a, b) => a.task.taskId - b.task.taskId);

    // Group consecutive same-type tasks together
    const groups: { kind: 'CA' | 'CR'; tasks: TaskSummary[] }[] = [];
    for (const item of allTasks) {
        const last = groups[groups.length - 1];
        if (last && last.kind === item.kind) {
            last.tasks.push(item.task);
        } else {
            groups.push({ kind: item.kind, tasks: [item.task] });
        }
    }

    // Pair groups into stages: each CA group followed by an optional CR group = one stage
    const stages: CompanyStage[] = [];
    let i = 0;
    while (i < groups.length) {
        const stage: CompanyStage = { approvers: [], reviewers: [] };
        if (groups[i].kind === 'CA') {
            stage.approvers = groups[i].tasks;
            i++;
            if (i < groups.length && groups[i].kind === 'CR') {
                stage.reviewers = groups[i].tasks;
                i++;
            }
        } else {
            // CR without a preceding CA (edge case)
            stage.reviewers = groups[i].tasks;
            i++;
        }
        stages.push(stage);
    }

    // If there's only one stage, return as-is (no multi-stage labeling needed)
    return stages;
}

// Auto-detect: is it a number or an email?
function detectSearchType(value: string): 'request' | 'email' | 'unknown' {
    const trimmed = value.trim();
    if (!trimmed) return 'unknown';
    if (/^\d+$/.test(trimmed)) return 'request';
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'email';
    return 'unknown';
}

// ─── SVG Icons ──────────────────────────────────────────────────────────────

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);
const XIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const UserPlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
    </svg>
);

// ─── Node Icons (enterprise SVG, color-aware) ────────────────────────────────
const NodeIcon: React.FC<{ type: string; size?: number; color?: string }> = ({ type, size = 22, color = '#6b7280' }) => {
    const s = { width: size, height: size };
    switch (type) {
        case 'submit':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/></svg>;
        case 'company':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>;
        case 'reviewer':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
        case 'gate-review':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>;
        case 'gate-approve':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
        case 'security':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'approved':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
        case 'rejected':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
        case 'finish':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
        case 'person':
            return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        default:
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8"><circle cx="12" cy="12" r="9"/></svg>;
    }
};

// ─── Status Badge ───────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, { bg: string; color: string; label: string }> = {
    COMPANY_APPROVER_PENDING: { bg: '#fde8e2', color: '#c0421e', label: 'Company Approver' },
    COMPANY_REVIEWER_PENDING: { bg: '#fde8e2', color: '#c0421e', label: 'Company Reviewer' },
    GATE_REVIEWER_PENDING:    { bg: '#f3e8ff', color: '#7c3aed', label: 'Gate Reviewer' },
    GATE_APPROVER_PENDING:    { bg: '#f3e8ff', color: '#7c3aed', label: 'Gate Approver' },
    SECURITY_APPROVER_PENDING:{ bg: '#fef3c7', color: '#b45309', label: 'Security Approver' },
    PARTIALLY_APPROVED:       { bg: '#f5f0ee', color: '#c0421e', label: 'Partially Approved' },
    SUBMITTED:                { bg: '#f0f0f0', color: '#3a3a3a', label: 'Submitted' },
    RETURNED:                 { bg: '#fef3c7', color: '#92400e', label: 'Returned' },
};
const StateBadge: React.FC<{ state: string }> = ({ state }) => {
    const badge = STATUS_BADGE[state] || { bg: '#f1f5f9', color: '#475569', label: friendlyState(state) };
    return (
        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.02em', fontFamily: FONT, backgroundColor: badge.bg, color: badge.color, whiteSpace: 'nowrap' }}>
            {badge.label}
        </span>
    );
};

// ─── Graph Styles ───────────────────────────────────────────────────────────

const STATUS_COLORS: Record<NodeStatus, { bg: string; border: string; text: string; glow: string }> = {
    completed: { bg: '#f7f7f7', border: '#c8c8c8', text: '#1a1a1a', glow: 'none' },
    active:    { bg: '#f0f4ff', border: '#3b82f6', text: '#1e3a8a', glow: '0 0 0 4px rgba(59,130,246,0.18)' },
    pending:   { bg: '#f4f4f4', border: '#c8c8c8', text: '#5a5a5a', glow: 'none' },
    rejected:  { bg: '#fef2f2', border: '#f87171', text: '#991b1b', glow: '0 0 0 3px rgba(248,113,113,0.15)' },
    returned:  { bg: '#fef3c7', border: '#fbbf24', text: '#92400e', glow: '0 0 0 3px rgba(251,191,36,0.15)' },
    skipped:   { bg: '#f0f0f0', border: '#d4d4d4', text: '#8a8a8a', glow: 'none' },
};

// ─── Graph Node ─────────────────────────────────────────────────────────────

interface NodeProps {
    icon: string; label: string; sublabel?: string; status: NodeStatus;
    performer?: string; timestamp?: string; compact?: boolean; onActiveClick?: () => void;
    statusLabel?: string; // Override the default status badge text
}
const GraphNode: React.FC<NodeProps> = ({ icon, label, sublabel, status, performer, timestamp, compact, onActiveClick, statusLabel }) => {
    const c = STATUS_COLORS[status];
    const isActive = status === 'active';
    const isClickable = isActive && !!onActiveClick;
    const [hovered, setHovered] = useState(false);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minWidth: compact ? '110px' : '140px', maxWidth: compact ? '130px' : '195px' }}>
            <div onClick={isClickable ? onActiveClick : undefined} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
                style={{
                    backgroundColor: c.bg, border: `2px solid ${isClickable && hovered ? '#2563eb' : c.border}`,
                    borderRadius: '10px', padding: compact ? '10px 12px' : '14px 16px', textAlign: 'center',
                    boxShadow: isClickable && hovered ? '0 0 0 4px rgba(37,99,235,0.18), 0 4px 12px rgba(37,99,235,0.10)' : c.glow,
                    width: '100%', transition: 'all 0.2s ease', cursor: isClickable ? 'pointer' : 'default',
                    transform: isClickable && hovered ? 'translateY(-1px)' : 'none',
                    ...(isActive && !hovered ? { animation: 'subtlePulse 2.5s ease-in-out infinite' } : {}),
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '5px' }}>
                    <NodeIcon type={icon} size={compact ? 18 : 22} color={c.text} />
                </div>
                <div style={{ fontWeight: '700', fontSize: compact ? '11px' : '12px', color: c.text, lineHeight: '1.3', fontFamily: FONT }}>{label}</div>
                {sublabel && <div style={{ fontSize: '10px', color: c.text, opacity: 0.7, marginTop: '1px', fontWeight: '600', fontFamily: FONT }}>{sublabel}</div>}
                <div style={{ marginTop: '5px', display: 'inline-block', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 8px', borderRadius: '4px', backgroundColor: c.border + '25', color: c.text, fontFamily: FONT }}>
                    {statusLabel || (status === 'active' ? '● In Progress' : status === 'completed' ? '✓ Done' : status === 'rejected' ? '✗ Rejected' : status === 'returned' ? '↩ Returned' : status === 'skipped' ? '— Skipped' : '○ Pending')}
                </div>
                {isClickable && (
                    <div style={{ marginTop: '5px', fontSize: '9px', color: '#3b82f6', fontWeight: '600', fontFamily: FONT, opacity: hovered ? 1 : 0.5, transition: 'opacity 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                        <UserPlusIcon /> Reassign
                    </div>
                )}
            </div>
            {(performer || timestamp) && (
                <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center', lineHeight: '1.4', fontFamily: FONT, maxWidth: '100%' }}>
                    {performer && (() => {
                        const names = performer.split(', ');
                        if (names.length <= 1) {
                            return <div style={{ fontWeight: '600', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'center' }}><NodeIcon type="person" color="#94a3b8" /> {names[0]}</div>;
                        }
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', alignItems: 'center' }}>
                                {names.map((name: string, i: number) => (
                                    <div key={i} style={{ fontWeight: '600', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'center' }}><NodeIcon type="person" color="#94a3b8" /> {name}</div>
                                ))}
                            </div>
                        );
                    })()}
                    {timestamp && <div>{timestamp}</div>}
                </div>
            )}
        </div>
    );
};

const VerticalConnector: React.FC<{ color?: string; height?: number }> = ({ color = '#d0ccc9', height = 28 }) => (
    <div style={{ width: '2px', height: `${height}px`, backgroundColor: color, margin: '0 auto' }} />
);

// ─── Reassign Modal ─────────────────────────────────────────────────────────

const ReassignModal: React.FC<{
    isOpen: boolean; onClose: () => void; context: ReassignContext | null;
    gatePassId: number; getAccessToken: () => Promise<string>; onSuccess: () => void;
}> = ({ isOpen, onClose, context, gatePassId, getAccessToken, onSuccess }) => {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { if (isOpen) { setEmail(''); setError(null); setSuccess(null); setSubmitting(false); setTimeout(() => inputRef.current?.focus(), 100); } }, [isOpen]);
    if (!isOpen || !context) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) { setError('Please enter an email address.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setError('Please enter a valid email address.'); return; }
        setSubmitting(true); setError(null); setSuccess(null);
        try {
            const accessToken = await getAccessToken();
            const res = await fetch(`${API_BASE_URL}/gatepass/${gatePassId}/reassign-task`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ taskType: context.taskType, newUserEmail: trimmed, gateId: context.gateId || undefined, gateNumber: context.gateNumber || undefined }),
            });
            const result = await res.json();
            if (result.success) { setSuccess(result.message || 'User assigned successfully.'); setTimeout(() => { onClose(); onSuccess(); }, 1500); }
            else { setError(result.message || result.error || 'Failed to assign user.'); }
        } catch { setError('Network error. Please try again.'); }
        finally { setSubmitting(false); }
    };

    return (
        <>
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(4px)', zIndex: 10000 }} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 10001, width: '95vw', maxWidth: '440px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', fontFamily: FONT }}>Assign New User</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px', fontFamily: FONT }}>{context.stageLabel} &middot; Request #{gatePassId}</div>
                    </div>
                    <button onClick={onClose} style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><XIcon /></button>
                </div>
                <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
                    <div style={{ backgroundColor: '#fdf5f3', border: '1px solid #f5c4b8', borderRadius: '8px', padding: '10px 14px', marginBottom: '18px', fontSize: '12px', color: '#a03520', lineHeight: '1.5', fontFamily: FONT }}>
                        A new pending task will be created for the specified user at this workflow stage. The existing assignee's task will remain unchanged.
                    </div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px', fontFamily: FONT, textTransform: 'uppercase', letterSpacing: '0.05em' }}>New User Email</label>
                    <input ref={inputRef} type="email" value={email} onChange={e => { setEmail(e.target.value); setError(null); }}
                        placeholder="user@company.com" disabled={submitting || !!success}
                        style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: `1px solid ${error ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box', fontFamily: FONT, color: '#1e293b', backgroundColor: '#fff', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                        onFocus={e => { if (!error) { e.currentTarget.style.borderColor = '#F3694A'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(243,105,74,0.10)'; } }}
                        onBlur={e => { if (!error) { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; } }}
                    />
                    {error && <div style={{ marginTop: '8px', fontSize: '12px', color: '#dc2626', fontFamily: FONT }}>{error}</div>}
                    {success && <div style={{ marginTop: '8px', fontSize: '12px', color: '#16a34a', fontFamily: FONT }}>✓ {success}</div>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px' }}>
                        <button type="button" onClick={onClose} disabled={submitting} style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: '#64748b', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
                        <button type="submit" disabled={submitting || !email.trim() || !!success} style={{ padding: '8px 20px', fontSize: '13px', fontWeight: '700', color: '#fff', backgroundColor: (submitting || !email.trim() || !!success) ? '#93c5fd' : '#ef4444', border: 'none', borderRadius: '8px', cursor: (submitting || !email.trim() || !!success) ? 'not-allowed' : 'pointer', fontFamily: FONT }}>
                            {submitting ? 'Assigning...' : success ? '✓ Assigned' : 'Assign User'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

// ─── Workflow Graph Content ─────────────────────────────────────────────────

const WorkflowGraphContent: React.FC<{ graphData: WorkflowGraphData; onReassign: (ctx: ReassignContext) => void }> = ({ graphData, onReassign }) => {
    const currentState = graphData.currentState || '';
    const isRejected = currentState === 'REJECTED';
    const isReturned = currentState === 'RETURNED';

    // Use chronological workflowPasses from backend
    const passes = graphData.workflowPasses || [];
    const hasAnyReturn = passes.some(p => p.returnEvent);

    // Detect skipped-by-user gates
    const isSkippedByUser = (g: GateBranch) => {
        if (g.exclusionReason === 'CANCELLED_BY_USER') return true;
        if (g.isExcluded) {
            const allTasks = [...g.reviewerTasks, ...g.approverTasks];
            return allTasks.length > 0 && allTasks.every(t => t.taskStatus === 'CANCELLED_BY_USER');
        }
        return false;
    };

    // Detect gates rejected by an approver/reviewer
    const isRejectedGate = (g: GateBranch) => {
        if (!g.isExcluded) return false;
        if (isSkippedByUser(g)) return false;
        if (g.exclusionReason && g.exclusionReason.startsWith('Rejected by')) return true;
        const allTasks = [...g.reviewerTasks, ...g.approverTasks];
        return allTasks.some(t => t.taskStatus === 'REJECTED');
    };

    const allGateBranches = passes.flatMap(p => p.gateBranches || []);
    const hasAnySkippedGates = allGateBranches.some(g => g.isExcluded && isSkippedByUser(g));
    const hasAnyRejectedGates = allGateBranches.some(g => isRejectedGate(g));

    const mkHandler = (status: NodeStatus, taskType: string, stageLabel: string, gateId?: number, gateNumber?: string) =>
        status === 'active' ? () => onReassign({ taskType, stageLabel, gateId, gateNumber }) : undefined;

    return (
        <>
            <style>{`@keyframes subtlePulse { 0%, 100% { box-shadow: 0 0 0 4px rgba(59,130,246,0.10); } 50% { box-shadow: 0 0 0 8px rgba(59,130,246,0.05); } }`}</style>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0' }}>

                <GraphNode icon="submit" label="Submitted" sublabel={graphData.submitter.companyName} status="completed"
                    performer={graphData.submitter.name || graphData.submitter.email?.split('@')[0]}
                    timestamp={formatTime(graphData.submitter.submittedAt)} />

                {/* ── CHRONOLOGICAL PASS-BASED RENDERING ── */}
                {passes.map((pass, pIdx) => {
                    const isLastPass = pIdx === passes.length - 1;

                    // ── Split CA/CR tasks into sequential stages ──
                    // This handles arbitrary CA→CR→CA→CR chains within a single pass,
                    // which happens when the same company appears as external1 AND external2.
                    const companyStages = splitPassIntoStages(pass.companyApprovers, pass.companyReviewers);
                    const multiStage = companyStages.length > 1;
                    const reviewerCompany = pass.reviewerCompanyName || pass.companyReviewers[0]?.assignedCompanyName || pass.companyName;
                    //const isDiffCompany = reviewerCompany && reviewerCompany !== pass.companyName;

                    // ── Gate branches for this pass ──
                    const passGateBranches = (pass.gateBranches || []).filter(g =>
                        g.reviewerTasks.length > 0 || g.approverTasks.length > 0 || isSkippedByUser(g) || isRejectedGate(g)
                    );
                    const hasGates = passGateBranches.length > 0;
                    const passSkippedGates = passGateBranches.filter(g => g.isExcluded && isSkippedByUser(g));
                    const passRejectedGates = passGateBranches.filter(g => isRejectedGate(g));
                    const passActiveBranches = passGateBranches.filter(g => !g.isExcluded);
                    const passHasSkippedGates = passSkippedGates.length > 0;
                    const passHasRejectedGates = passRejectedGates.length > 0;
                    const passHasExcludedGates = passHasSkippedGates || passHasRejectedGates;

                    // lastCompanyDone: determined from the LAST stage's tasks
                    const lastStage = companyStages[companyStages.length - 1];
                    const lastCompanyDone = lastStage
                        ? (lastStage.reviewers.length > 0
                            ? lastStage.reviewers.some(t => t.taskStatus === 'COMPLETED')
                            : lastStage.approvers.some(t => t.taskStatus === 'COMPLETED'))
                        : false;

                    // ── Security status ──
                    const hasSecurity = pass.securityTasks.length > 0;
                    let secStatus = hasSecurity
                        ? resolveTaskGroupStatus(pass.securityTasks, currentState, ['SECURITY_APPROVER_PENDING'])
                        : null;
                    if (secStatus && pass.returnEvent?.stage === 'SECURITY_APPROVER' && secStatus === 'completed') secStatus = 'returned' as NodeStatus;
                    if (secStatus && isLastPass && isReturned && secStatus === 'active') secStatus = 'returned' as NodeStatus;

                    return (
                        <React.Fragment key={`pass-${pass.passNumber}`}>
                            {/* ── Submitter resubmit between passes ── */}
                            {pass.resubmitEvent && (
                                <>
                                    <VerticalConnector color="#F3694A" height={20} />
                                    <GraphNode icon="submit" label="Submitter" sublabel={graphData.submitter.companyName}
                                        status="completed" statusLabel="✓ Resubmitted"
                                        performer={pass.resubmitEvent.performer || graphData.submitter.name || graphData.submitter.email?.split('@')[0]}
                                        timestamp={pass.resubmitEvent.timestamp ? formatTime(pass.resubmitEvent.timestamp) : undefined} />
                                </>
                            )}

                            <VerticalConnector color={pIdx === 0 || pass.resubmitEvent ? '#F3694A' : '#d0ccc9'} />

                            {passes.length > 1 && (
                                <div style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '4px', fontFamily: FONT }}>
                                    Cycle {pass.passNumber} &middot; {pass.companyName}
                                </div>
                            )}

                            {/* ── Render each CA→CR stage sequentially ── */}
                            {companyStages.map((stage, sIdx) => {
                                const isFirstStage = sIdx === 0;
                                const hasApprovers = stage.approvers.length > 0;
                                const hasReviewers = stage.reviewers.length > 0;

                                // Approver status
                                let caStatus = hasApprovers
                                    ? resolveTaskGroupStatus(stage.approvers, currentState, ['COMPANY_APPROVER_PENDING'])
                                    : null;
                                if (caStatus && pass.returnEvent?.stage === 'COMPANY_APPROVER' && caStatus === 'completed') caStatus = 'returned' as NodeStatus;
                                if (caStatus && isLastPass && isReturned && caStatus === 'active') caStatus = 'returned' as NodeStatus;
                                if (caStatus && isLastPass && isRejected && caStatus === 'active') caStatus = 'rejected' as NodeStatus;

                                // Reviewer status
                                let crStatus = hasReviewers
                                    ? resolveTaskGroupStatus(stage.reviewers, currentState, ['COMPANY_REVIEWER_PENDING'])
                                    : null;
                                if (crStatus && pass.returnEvent?.stage === 'COMPANY_REVIEWER' && crStatus === 'completed') crStatus = 'returned' as NodeStatus;
                                if (crStatus && isLastPass && isReturned && crStatus === 'active') crStatus = 'returned' as NodeStatus;
                                if (crStatus && isLastPass && isRejected && crStatus === 'active') crStatus = 'rejected' as NodeStatus;

                                const caDone = hasApprovers && stage.approvers.some(t => t.taskStatus === 'COMPLETED');

                                // Per-stage reviewer company
                                const stageReviewerCompany = stage.reviewers[0]?.assignedCompanyName || reviewerCompany;

                                // Stage label suffixes for multi-stage passes
                                // First approver (from submitter's company) has no stage.
                                // Each reviewer starts a new stage number.
                                // The approver AFTER a reviewer shares that reviewer's stage number.
                                const approverStageTag = multiStage && sIdx > 0 ? ` (Stage ${sIdx})` : '';
                                const reviewerStageTag = multiStage ? ` (Stage ${sIdx + 1})` : '';

                                return (
                                    <React.Fragment key={`stage-${sIdx}`}>
                                        {/* Connector between stages (not before the first stage) */}
                                        {!isFirstStage && (
                                            (() => {
                                                const prev = companyStages[sIdx - 1];
                                                const prevDone = prev.reviewers.length > 0
                                                    ? prev.reviewers.some(t => t.taskStatus === 'COMPLETED')
                                                    : prev.approvers.some(t => t.taskStatus === 'COMPLETED');
                                                return <VerticalConnector color={prevDone ? '#F3694A' : '#d0ccc9'} />;
                                            })()
                                        )}

                                        {/* Company Approver */}
                                        {hasApprovers && caStatus !== null && (() => {
                                            // For sIdx > 0 (post-reviewer stages), the approver belongs to the
                                            // reviewer's company, not the original submitter's company.
                                            const approverCompany = sIdx > 0
                                                ? (stage.approvers[0]?.assignedCompanyName || stageReviewerCompany)
                                                : pass.companyName;
                                            return (
                                                <GraphNode icon="company"
                                                    label={`Company Approver${approverStageTag}`}
                                                    sublabel={approverCompany}
                                                    status={caStatus}
                                                    performer={getPerformerName(stage.approvers)}
                                                    timestamp={getCompletedTime(stage.approvers)}
                                                    onActiveClick={mkHandler(caStatus, 'COMPANY_APPROVER', `Company Approver – ${approverCompany}`)} />
                                            );
                                        })()}

                                        {/* Company Reviewer */}
                                        {hasReviewers && crStatus !== null && (
                                            <>
                                                {hasApprovers && (
                                                    <>
                                                        <VerticalConnector color={caDone ? '#F3694A' : '#d0ccc9'} height={20} />
                                                        <div style={{
                                                            display: 'flex', alignItems: 'center', gap: '10px',
                                                            width: '100%', maxWidth: '195px', margin: '0 auto 4px',
                                                        }}>
                                                            <div style={{ flex: 1, height: '1.5px', backgroundColor: caDone ? '#F3694A' : '#d0ccc9' }} />
                                                            <div style={{
                                                                fontSize: '9px', fontWeight: '700', textTransform: 'uppercase',
                                                                letterSpacing: '0.08em', color: '#94a3b8', whiteSpace: 'nowrap',
                                                                fontFamily: FONT,
                                                            }}>
                                                                {multiStage || stageReviewerCompany !== pass.companyName
                                                                    ? `→ ${stageReviewerCompany}`
                                                                    : 'Review Stage'}
                                                            </div>
                                                            <div style={{ flex: 1, height: '1.5px', backgroundColor: caDone ? '#F3694A' : '#d0ccc9' }} />
                                                        </div>
                                                        <VerticalConnector color={caDone ? '#F3694A' : '#d0ccc9'} height={10} />
                                                    </>
                                                )}
                                                <GraphNode icon="reviewer"
                                                    label={`Company Reviewer${reviewerStageTag}`}
                                                    sublabel={stageReviewerCompany}
                                                    status={crStatus}
                                                    performer={getPerformerName(stage.reviewers)}
                                                    timestamp={getCompletedTime(stage.reviewers)}
                                                    onActiveClick={mkHandler(crStatus, 'COMPANY_REVIEWER', `Company Reviewer – ${stageReviewerCompany}`)} />
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })}

                            {/* ── Gate Branches ── */}
                            {hasGates && (() => {
                                const n = passGateBranches.length;
                                return (<>
                                    <VerticalConnector color={lastCompanyDone ? '#F3694A' : '#d0ccc9'} height={20} />
                                    <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: '8px', fontFamily: FONT }}>{n > 1 ? `${n} Gate Branches` : 'Gate Processing'}</div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', padding: '12px 0', position: 'relative' }}>
                                        {n > 1 && <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: `${Math.min(n * 160, 700)}px`, maxWidth: '90%', height: '2px', backgroundColor: lastCompanyDone ? '#F3694A' : '#d0ccc9' }} />}
                                        {passGateBranches.map(b => {
                                            const isSkipped = isSkippedByUser(b);
                                            const isRejected = isRejectedGate(b);
                                            const isDimmed = isSkipped || isRejected;
                                            // Allow gate reviewer to be active when workflow is in any gate processing state
const rv = isSkipped ? 'skipped' as NodeStatus : resolveTaskGroupStatus(b.reviewerTasks, currentState, ['GATE_REVIEWER_PENDING', 'GATE_APPROVER_PENDING']);

// Gate approver: active in any gate state, but only if this branch's reviewer is done
const branchReviewerDone = b.reviewerTasks.length > 0
    && b.reviewerTasks.some(t => t.taskStatus === 'COMPLETED')
    && !b.reviewerTasks.some(t => t.taskStatus === 'PENDING');

    const approverActiveStates = branchReviewerDone
    ? ['GATE_APPROVER_PENDING', 'GATE_REVIEWER_PENDING']
    : ['GATE_APPROVER_PENDING'];
const ap = b.approverTasks.length > 0 ? (isSkipped ? 'skipped' as NodeStatus : resolveTaskGroupStatus(b.approverTasks, currentState, approverActiveStates)) : null;
                                            return (
                                                <div key={b.gateId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: isRejected ? '#fef2f2' : isSkipped ? '#f0f0f0' : '#f7f7f7', border: `1px solid ${isRejected ? '#fca5a5' : isSkipped ? '#d4d4d4' : '#d4d4d4'}`, borderRadius: '10px', padding: '14px 12px 10px', minWidth: '140px', opacity: isDimmed ? 0.65 : 1 }}>
                                                    <div style={{ fontSize: '10px', fontWeight: '700', color: isDimmed ? '#9ca3af' : '#4a4a4a', backgroundColor: isRejected ? '#fee2e2' : isSkipped ? '#e8e8e8' : '#e8e8e8', padding: '2px 10px', borderRadius: '4px', marginBottom: isDimmed ? '4px' : '10px', fontFamily: FONT, textDecoration: isDimmed ? 'line-through' : 'none' }}>Gate {b.gateNumber}</div>
                                                    {isRejected && <div style={{ fontSize: '9px', fontWeight: '700', color: '#991b1b', backgroundColor: '#fee2e2', padding: '2px 8px', borderRadius: '8px', marginBottom: '8px', letterSpacing: '0.04em', fontFamily: FONT }}>✕ REJECTED</div>}
                                                    {isSkipped && <div style={{ fontSize: '9px', fontWeight: '700', color: '#92400e', backgroundColor: '#fef3c7', padding: '2px 8px', borderRadius: '8px', marginBottom: '8px', letterSpacing: '0.04em', fontFamily: FONT }}>— SKIPPED</div>}
                                                    {b.reviewerTasks.length > 0 && (<><GraphNode compact icon="gate-review" label="Gate Reviewer" sublabel={`Gate ${b.gateNumber}`} status={rv} performer={isSkipped ? undefined : getPerformerName(b.reviewerTasks)} timestamp={isSkipped ? undefined : getCompletedTime(b.reviewerTasks)} onActiveClick={isSkipped ? undefined : mkHandler(rv, 'GATE_REVIEWER', `Gate Reviewer – Gate ${b.gateNumber}`, b.gateId, b.gateNumber)} />{ap !== null && <VerticalConnector color={isSkipped ? '#d4d4d4' : (rv === 'completed' ? '#F3694A' : '#d0ccc9')} height={16} />}</>)}
                                                    {ap !== null && <GraphNode compact icon="gate-approve" label="Gate Approver" sublabel={`Gate ${b.gateNumber}`} status={ap} performer={isSkipped ? undefined : getPerformerName(b.approverTasks)} timestamp={isSkipped ? undefined : getCompletedTime(b.approverTasks)} onActiveClick={isSkipped ? undefined : mkHandler(ap, 'GATE_APPROVER', `Gate Approver – Gate ${b.gateNumber}`, b.gateId, b.gateNumber)} />}
                                                    {isSkipped && b.reviewerTasks.length === 0 && b.approverTasks.length === 0 && <div style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', fontFamily: FONT }}>Cancelled by user</div>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {(() => {
                                        const activeBranches = passGateBranches.filter(b => !b.isExcluded && !isRejectedGate(b));
                                        const allActiveBranchesDone = activeBranches.every(b =>
                                            (b.approverTasks.length === 0 || b.approverTasks.some(t => t.taskStatus === 'COMPLETED')) &&
                                            (b.reviewerTasks.length === 0 || b.reviewerTasks.some(t => t.taskStatus === 'COMPLETED'))
                                        );
                                        if (passHasExcludedGates && allActiveBranchesDone) {
                                            return (<>
                                        <VerticalConnector color={passHasRejectedGates ? '#ef4444' : '#f59e0b'} height={12} />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: passHasRejectedGates ? '#fef2f2' : '#fffbeb', border: `1.5px solid ${passHasRejectedGates ? '#fca5a5' : '#f59e0b'}`, borderRadius: '10px', padding: '8px 16px', boxShadow: `0 0 0 3px ${passHasRejectedGates ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)'}` }}>
                                            <span style={{ display:'flex', alignItems:'center', flexShrink:0 }}>{passHasRejectedGates ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>}</span>
                                            <div>
                                                <div style={{ fontSize: '11px', fontWeight: '700', color: passHasRejectedGates ? '#991b1b' : '#92400e', fontFamily: FONT }}>Proceeded with Approved Gates</div>
                                                <div style={{ fontSize: '9px', color: passHasRejectedGates ? '#b91c1c' : '#b45309', fontFamily: FONT }}>
                                                    {passActiveBranches.length} gate{passActiveBranches.length !== 1 ? 's' : ''} approved{passRejectedGates.length > 0 ? ` · ${passRejectedGates.length} gate${passRejectedGates.length !== 1 ? 's' : ''} rejected` : ''}{passSkippedGates.length > 0 ? ` · ${passSkippedGates.length} gate${passSkippedGates.length !== 1 ? 's' : ''} skipped` : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <VerticalConnector color={passHasRejectedGates ? '#ef4444' : '#f59e0b'} height={12} />
                                            </>);
                                        }
                                        const col = (!passHasExcludedGates && allActiveBranchesDone) ? '#F3694A' : '#d0ccc9';
                                        return (<><VerticalConnector color={col} height={12} /><div style={{ width: `${Math.min(n * 160, 900)}px`, maxWidth: '90%', height: '2px', backgroundColor: col, margin: '0 auto' }} /><VerticalConnector color={col} height={12} /></>);
                                    })()}
                                </>);
                            })()}

                            {/* ── Security Approver ── */}
                            {hasSecurity && secStatus !== null && (
                                <>
                                    {!hasGates && <VerticalConnector color={lastCompanyDone ? '#F3694A' : '#d0ccc9'} />}
                                    <GraphNode icon="security" label="Security Approver" status={secStatus}
                                        performer={getPerformerName(pass.securityTasks)}
                                        timestamp={getCompletedTime(pass.securityTasks)}
                                        onActiveClick={mkHandler(secStatus, 'SECURITY_APPROVER', 'Security Approver')} />
                                </>
                            )}
                        </React.Fragment>
                    );
                })}

                {/* ── FINAL NODE ── */}
                <VerticalConnector color={isReturned || isRejected || currentState === 'FULLY_APPROVED' ? '#F3694A' : '#d0ccc9'} />
                {isReturned ? (
                    <GraphNode icon="submit" label="Submitter" sublabel={graphData.submitter.companyName}
                        status="active"
                        performer={graphData.submitter.name || graphData.submitter.email?.split('@')[0]} />
                ) : (
                    <GraphNode icon={currentState === 'FULLY_APPROVED' ? 'approved' : isRejected ? 'rejected' : 'finish'}
                        label={currentState === 'FULLY_APPROVED' ? 'Accepted for Processing' : isRejected ? 'Rejected' : 'Accepted for Processing'}
                        status={currentState === 'FULLY_APPROVED' ? 'completed' : isRejected ? 'rejected' : 'pending'} />
                )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                {([ { status: 'completed' as NodeStatus, label: 'Completed' }, { status: 'active' as NodeStatus, label: 'In Progress (click to reassign)' }, { status: 'pending' as NodeStatus, label: 'Pending' },
                    ...(isRejected ? [{ status: 'rejected' as NodeStatus, label: 'Rejected' }] : []), ...(hasAnyReturn || isReturned ? [{ status: 'returned' as NodeStatus, label: 'Returned' }] : []),
                    ...(hasAnySkippedGates ? [{ status: 'skipped' as NodeStatus, label: 'Skipped by User' }] : []),
                    ...(hasAnyRejectedGates && !isRejected ? [{ status: 'rejected' as NodeStatus, label: 'Gate Rejected' }] : []),
                ]).map(item => (
                    <div key={item.status} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b', fontFamily: FONT }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: STATUS_COLORS[item.status].border }} />{item.label}
                    </div>
                ))}
            </div>
        </>
    );
};

// ─── Main Analytics View ────────────────────────────────────────────────────

export const AnalyticsView: React.FC<AnalyticsViewProps> = ({ getAccessToken, onStatusUpdate }) => {
    const [searchValue, setSearchValue] = useState('');
    const [emailResults, setEmailResults] = useState<EmailSearchResult[] | null>(null);
    const [emailSearching, setEmailSearching] = useState(false);
    const [emailSearchedFor, setEmailSearchedFor] = useState('');
    const [searchedId, setSearchedId] = useState<number | null>(null);
    const [graphData, setGraphData] = useState<WorkflowGraphData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignContext, setReassignContext] = useState<ReassignContext | null>(null);
    const [helpVisible, setHelpVisible] = useState(() => {
        try {
            const stored = localStorage.getItem('gateqx-help-visible-track');
            return stored !== null ? stored === 'true' : true;
        } catch { return true; }
    });
const inputRef = useRef<HTMLInputElement>(null);

    // Persist help guide visibility
    React.useEffect(() => {
        try { localStorage.setItem('gateqx-help-visible-track', String(helpVisible)); } catch {}
    }, [helpVisible]);

    const detectedType = detectSearchType(searchValue);
    const hintText = !searchValue.trim() ? '' : detectedType === 'request' ? 'Request #' : detectedType === 'email' ? 'Submitter email' : '';

    const fetchWorkflowGraph = useCallback(async (gatePassId: number) => {
        setLoading(true); setError(null); setGraphData(null); setSearchedId(gatePassId);
        try {
            const token = await getAccessToken();
            const [graphRes, auditRes] = await Promise.all([
                fetch(`${API_BASE_URL}/gatepass/${gatePassId}/workflow-graph`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_BASE_URL}/gatepass/${gatePassId}/audit-trail`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null),
            ]);
            const result = await graphRes.json();
            if (result.success) {
                // Merge audit trail if not already present in graph data
                if (!result.data.auditTrail && auditRes && auditRes.ok) {
                    try {
                        const auditResult = await auditRes.json();
                        if (auditResult.success && auditResult.data) {
                            result.data.auditTrail = Array.isArray(auditResult.data) ? auditResult.data : auditResult.data.entries || [];
                        }
                    } catch (e) { console.log('⚠️ Could not parse audit trail:', e); }
                }
                setGraphData(result.data);
            } else {
                const msg = result.error || 'Failed to load workflow data.';
                setError(msg.replace(/Workflow not found/i, 'Request not found'));
            }
        } catch { setError('Network error loading workflow data.'); }
        finally { setLoading(false); }
    }, [getAccessToken]);

    const searchByEmail = useCallback(async (email: string) => {
        setEmailSearching(true); setEmailResults(null); setEmailSearchedFor(email);
        setSearchedId(null); setGraphData(null); setError(null);
        try {
            const token = await getAccessToken();
            const res = await fetch(`${API_BASE_URL}/analytics/search-by-submitter/${encodeURIComponent(email)}`, { headers: { 'Authorization': `Bearer ${token}` } });
            const result = await res.json();
            if (result.success) setEmailResults(result.data); else setError(result.error || 'Search failed.');
        } catch { setError('Network error. Please try again.'); }
        finally { setEmailSearching(false); }
    }, [getAccessToken]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const val = searchValue.trim();
        if (!val) return;
        const type = detectSearchType(val);
        if (type === 'request') {
            setEmailResults(null); setEmailSearchedFor('');
            fetchWorkflowGraph(parseInt(val, 10));
        } else if (type === 'email') {
            searchByEmail(val.toLowerCase());
        } else {
            setError('Enter a request number (e.g. 1042) or a submitter email (e.g. user@company.com).');
        }
    };

    const handleClear = () => {
        setSearchValue(''); setEmailResults(null); setEmailSearchedFor('');
        setSearchedId(null); setGraphData(null); setError(null);
    };

    const handleResultClick = (id: number) => { setSearchedId(id); fetchWorkflowGraph(id); };
    const handleReassign = (ctx: ReassignContext) => { setReassignContext(ctx); setReassignOpen(true); };
    const handleReassignSuccess = () => { if (searchedId) fetchWorkflowGraph(searchedId); };

    const hasResults = emailSearchedFor || searchedId || loading || error || graphData;

    return (
        
        <>
        <h1 style={{
            fontSize: '25px',
            fontWeight: 600,
            color: '#1a1a1a',
            margin: '0 0 12px 0',
            padding: '5px 0 0 20px',
            lineHeight: 1.3,
            fontFamily: "'Segoe UI', sans-serif",
            letterSpacing: '-0.01em',
        }}>
            Track & Reassign Requests
        </h1>
        <div style={{ display: 'flex', gap: '0px', alignItems: 'flex-start' }}>
        {/* Left: Main content */}
        <div style={{ flex: 1, minWidth: 0, fontFamily: FONT }}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

            {/* ═══════════ UNIFIED SEARCH BAR ═══════════ */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '24px', paddingTop: '16px', paddingLeft: '20px' }}>
                <form onSubmit={handleSearch} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0',
                    padding: '6px 6px 6px 16px', width: '100%', maxWidth: '600px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}>
                    <span style={{ color: '#94a3b8', flexShrink: 0, display: 'flex' }}><SearchIcon /></span>
                    <input ref={inputRef} type="text" value={searchValue}
                        onChange={e => { setSearchValue(e.target.value); setError(null); }}
                        placeholder="Request number or submitter email..."
                        style={{
                            flex: 1, padding: '8px 4px', fontSize: '14px', border: 'none', outline: 'none',
                            fontFamily: FONT, color: '#1e293b', backgroundColor: 'transparent', minWidth: 0,
                        }}
                    />
                    {/* Auto-detect hint */}
                    {hintText && (
                        <span style={{
                            fontSize: '11px', color: '#94a3b8', backgroundColor: '#f1f5f9',
                            padding: '3px 8px', borderRadius: '4px', whiteSpace: 'nowrap', fontWeight: '600', flexShrink: 0,
                        }}>{hintText}</span>
                    )}
                    <button type="submit" disabled={!searchValue.trim() || loading || emailSearching}
                        style={{
                            padding: '9px 22px', fontSize: '13px', fontWeight: '700', color: '#fff',
                            backgroundColor: (!searchValue.trim() || loading || emailSearching) ? '#94a3b8' : '#ef4444',
                            border: 'none', borderRadius: '8px',
                            cursor: (!searchValue.trim() || loading || emailSearching) ? 'not-allowed' : 'pointer',
                            fontFamily: FONT, whiteSpace: 'nowrap', flexShrink: 0, transition: 'background-color 0.2s',
                        }}>
                        {(loading || emailSearching) ? 'Searching...' : 'Search'}
                    </button>
                    {hasResults && (
                        <button type="button" onClick={handleClear} title="Clear"
                            style={{
                                width: '34px', height: '34px', border: '1px solid #e2e8f0', borderRadius: '8px',
                                backgroundColor: '#fff', color: '#94a3b8', cursor: 'pointer', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            onMouseOver={e => { e.currentTarget.style.backgroundColor = '#faf7f6'; }}
                            onMouseOut={e => { e.currentTarget.style.backgroundColor = '#fff'; }}
                        ><XIcon /></button>
                    )}
                </form>
            </div>



            {/* ═══════════ EMAIL RESULTS TABLE ═══════════ */}
            {emailSearchedFor && emailResults !== null && (
                <div style={{ backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '24px', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', fontFamily: FONT }}>Active Requests</span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#fff', backgroundColor: '#ef4444', padding: '2px 8px', borderRadius: '10px', fontFamily: FONT }}>{emailResults.length}</span>
                        </div>
                        <span style={{ fontSize: '12px', color: '#94a3b8', fontFamily: FONT }}>
                            Submitted by <span style={{ fontWeight: '700', color: '#475569' }}>{emailSearchedFor}</span>
                        </span>
                    </div>

                    {emailResults.length === 0 ? (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', fontFamily: FONT }}>No active requests found for this email address.</div>
                    ) : (<>
                        {/* Column headers */}
                        <div style={{
                            display: 'grid', gridTemplateColumns: '72px 1fr 1fr 160px 130px',
                            padding: '10px 20px', borderBottom: '1px solid #f1f5f9',
                            fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase',
                            letterSpacing: '0.06em', fontFamily: FONT,
                        }}>
                            <div>Request</div><div>Organization</div><div>Pass Identifier</div><div>Current Stage</div><div>Submitted</div>
                        </div>
                        {/* Rows */}
                        <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                            {emailResults.map(r => {
                                const sel = searchedId === r.id;
                                return (
                                    <div key={r.id} onClick={() => handleResultClick(r.id)}
                                        style={{
                                            display: 'grid', gridTemplateColumns: '72px 1fr 1fr 160px 130px',
                                            padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid #f4f1f0',
                                            backgroundColor: sel ? '#fde8e2' : '#fff',
                                            borderLeft: sel ? '3px solid #F3694A' : '3px solid transparent',
                                            transition: 'all 0.12s ease', alignItems: 'center',
                                        }}
                                        onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = '#faf7f6'; }}
                                        onMouseLeave={e => { if (!sel) e.currentTarget.style.backgroundColor = '#fff'; }}
                                    >
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: sel ? '#F3694A' : '#1e293b', fontFamily: FONT }}>#{r.id}</div>
                                        <div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155', fontFamily: FONT }}>{r.companyName}</div>
                                            {r.consignmentDetails && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px', fontFamily: FONT, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.consignmentDetails}</div>}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#475569', fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.passIdentificationName || '—'}
                                        </div>
                                        <div><StateBadge state={r.currentState} /></div>
                                        <div style={{ fontSize: '12px', color: '#64748b', fontFamily: FONT }}>{formatDate(r.submittedAt)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </>)}
                </div>
            )}

            {/* ═══════════ WORKFLOW GRAPH ═══════════ */}
            {(loading || (error && (graphData || searchedId)) || graphData) && (
                <div style={{ backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', fontFamily: FONT }}>Workflow Progress</span>
                            {searchedId && <span style={{ fontSize: '12px', fontWeight: '700', color: '#F3694A', backgroundColor: '#fde8e2', padding: '3px 10px', borderRadius: '4px', fontFamily: FONT }}>#{searchedId}</span>}
                        </div>
                        {graphData && <div style={{ fontSize: '12px', color: '#64748b', fontFamily: FONT }}>{graphData.submitter.name}{graphData.submitter.companyName ? ` · ${graphData.submitter.companyName}` : ''}</div>}
                    </div>
                    <div style={{ padding: '24px 20px 28px' }}>
                        {loading && (
                            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
                                <div style={{ display: 'inline-block', width: '28px', height: '28px', border: '3px solid #e8e8e8', borderTopColor: '#F3694A', borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginBottom: '12px' }} />
                                <div style={{ fontSize: '13px', fontFamily: FONT }}>Loading workflow graph...</div>
                            </div>
                        )}
                        {error && !loading && (
    <div style={{ textAlign: 'center', padding: '32px 20px' }}>
        <div style={{ fontSize: '13px', color: '#991b1b', marginBottom: '12px', fontFamily: FONT }}>{error}</div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            {/* Only show Retry if this could be a transient network error */}
            {error.toLowerCase().includes('network') && (
                <button onClick={() => searchedId && fetchWorkflowGraph(searchedId)}
                    style={{ padding: '7px 18px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: FONT }}>
                    Retry
                </button>
            )}
            <button onClick={() => {
                setError(null); setGraphData(null); setSearchedId(null);
                setSearchValue('');
                setTimeout(() => inputRef.current?.focus(), 50);
            }}
                style={{ padding: '7px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: FONT }}>
                Try a different number
            </button>
        </div>
    </div>
)}
                        {graphData && !loading && !error && <WorkflowGraphContent graphData={graphData} onReassign={handleReassign} />}
                    </div>
                </div>
            )}

            {/* ═══════════ EMPTY STATE ═══════════ */}
            {!loading && !error && !graphData && !emailSearchedFor && (
                <div style={{ textAlign: 'center', padding: '80px 0', color: '#d0ccc9', maxWidth: '600px', marginLeft: '20px' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block' }}>
                        <circle cx="12" cy="3" r="2" />
                        <line x1="12" y1="5" x2="12" y2="10" />
                        <circle cx="12" cy="12" r="2" />
                        <line x1="12" y1="14" x2="6" y2="19" />
                        <line x1="12" y1="14" x2="18" y2="19" />
                        <circle cx="6" cy="21" r="2" />
                        <circle cx="18" cy="21" r="2" />
                    </svg>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#94a3b8', fontFamily: FONT }}>
                        Enter a request number or submitter email to begin
                    </div>
                    <div style={{ fontSize: '12px', color: '#d0ccc9', marginTop: '4px', fontFamily: FONT }}>
                        The system auto-detects whether you're searching by number or email
                    </div>
                </div>
            )}

            <ReassignModal isOpen={reassignOpen} onClose={() => setReassignOpen(false)} context={reassignContext}
                gatePassId={searchedId || 0} getAccessToken={getAccessToken} onSuccess={handleReassignSuccess} />
        </div>

        {/* Right: Help Guide — animated collapse */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            width: helpVisible ? 310 : 44,
            padding: '28px 0 32px 0',
            transition: 'width 0.3s ease',
            overflow: 'hidden',
          }}
        >
          {/* Help content */}
          <div
            style={{
              flex: helpVisible ? 1 : '0 0 0px',
              opacity: helpVisible ? 1 : 0,
              overflow: 'hidden',
              transition: 'opacity 0.25s ease, flex 0.3s ease',
              pointerEvents: helpVisible ? 'auto' : 'none',
              minWidth: 0,
              padding: helpVisible ? '0 16px 0 28px' : '0',
            }}
          >
            <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, color: '#9ca3af' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', margin: '0 0 8px 0', fontFamily: FONT }}>Track & Reassign</p>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 18px 0', lineHeight: 1.6, fontFamily: FONT }}>
              Search for any gate pass request by its number or by the submitter's email address. The system auto-detects the search type.
            </p>
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', margin: '0 0 6px 0', fontFamily: FONT }}>Search by Email</p>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 18px 0', lineHeight: 1.6, fontFamily: FONT }}>
              Enter an email to see all active requests submitted by that person. Click any row to load its workflow graph.
            </p>
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', margin: '0 0 6px 0', fontFamily: FONT }}>Workflow Graph</p>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 18px 0', lineHeight: 1.6, fontFamily: FONT }}>
              Visualises the full approval workflow for a request — from submission through company and gate stages to security approval.
            </p>
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', margin: '0 0 6px 0', fontFamily: FONT }}>Reassign Tasks</p>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 0 0', lineHeight: 1.6, fontFamily: FONT }}>
              Blue "In Progress" nodes can be clicked to reassign the task to a different user within the same company and role.
            </p>
          </div>

          {/* Toggle button */}
          <button
            type="button"
            onClick={() => setHelpVisible(!helpVisible)}
            aria-label={helpVisible ? 'Hide Guide' : 'Show Guide'}
            title={helpVisible ? 'Hide Guide' : 'Show Guide'}
            style={helpVisible ? {
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              writingMode: 'vertical-rl' as const,
              textOrientation: 'mixed' as const,
              background: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRight: 'none',
              borderRadius: '6px 0 0 6px',
              padding: '12px 7px',
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              cursor: 'pointer',
              letterSpacing: '0.02em',
              flexShrink: 0,
              alignSelf: 'flex-start',
              marginTop: 36,
              zIndex: 2,
              transition: 'background 0.15s ease',
              fontFamily: FONT,
            } : {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '50%',
              border: '1.5px solid #d4d4d4',
              background: '#ffffff',
              cursor: 'pointer',
              flexShrink: 0,
              margin: '6px auto 0 auto',
              padding: 0,
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (helpVisible) {
                e.currentTarget.style.background = '#ebebeb';
              } else {
                e.currentTarget.style.borderColor = '#F3694A';
                e.currentTarget.style.background = '#fef7f5';
              }
            }}
            onMouseLeave={(e) => {
              if (helpVisible) {
                e.currentTarget.style.background = '#f5f5f5';
              } else {
                e.currentTarget.style.borderColor = '#d4d4d4';
                e.currentTarget.style.background = '#ffffff';
              }
            }}
          >
            {helpVisible ? (
              <>
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0, transform: 'rotate(90deg)' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M15 9l-6 3 6 3" />
                </svg>
                <span>Hide Guide</span>
              </>
            ) : (
              <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="#848d9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            )}
          </button>
        </div>
        </div>
        </>
    );
};

export default AnalyticsView;